import { ReactElement, useEffect, useMemo, useRef } from "react";
import parse from "html-react-parser";
import { Box, VBox } from "./Box";
import { LoadingIndicator } from "./LoadingIndicator";
import { LoadingOverlay } from "./LoadingOverlay";
import { classNames } from "@/utils/classNames";
import {
  Button,
  LinkButton,
  LinkButtonProps,
  ToggleButtonGroup,
  ToggleButtonProps,
} from "./Button";
import { isMobileView } from "@/utils/screen";
import { EmptyState } from "./EmptyState";

import { useTranslation } from "@/stores/RootStoreContext";
import { DownloadAsPdfButton, DownloadAsPdfButtonProps } from "@/common/commonComponents";
import { when } from "@/utils/functions";
import { PdfPreview } from "./PdfPreview";
import { UploadPdfPlaceholder } from "./UploadFilesPanel";
import { FileUploadConfig, UploadedAttachment } from "@/fileUpload/fileUploadDomain";
import { isBlank } from "@/utils/strings";
import { observer } from "mobx-react";
import { AttachmentPreview, UploadingAttachment } from "@/dbot/common/commonDomain";
import { isArray } from "lodash";
import { FilePreview } from "./FilePreview";
import { AttachmentListItem } from "./AttachmentsPanel";
import { showsFilePreview, UploadedAttachmentProps } from "@/dbot/attachments";

const formatContent = (content: string) =>
  content
    .replace(/max-width:.*?px;/, "")
    .replaceAll(
      /<div.*?page-break-before:.*?><\/div>/g,
      "<hr style='border:none; border-top: 2px dashed #aaa; margin: 50px 0'/>",
    );

type DocumentViewProps = {
  content?: string;
  download?: DownloadAsPdfButtonProps;
  padding?: boolean;
  dropShadow?: boolean;
  extraAction?: ["link", LinkButtonProps] | ["toggle-buttons", ToggleButtonProps[]];
};

function DocumentView({ content, download, padding, dropShadow, extraAction }: DocumentViewProps) {
  const parsed = useMemo(() => content && parse(formatContent(content)), [content]);

  return (
    <VBox paddingX={!isMobileView() && padding} grow paddingBottom>
      <VBox dropShadow={!isMobileView() && dropShadow} bg className={"relative lg:p-12 xl:p-24"}>
        <div className={classNames("revert-all", (download || extraAction) && "pt-6")}>
          {parsed && <div className={"content"}>{parsed}</div>}
        </div>
        {extraAction && (
          <Box className={"absolute top-4 left-8"}>
            {when(extraAction, ([type, props]) => {
              switch (type) {
                case "link":
                  return <LinkButton {...props} />;
                case "toggle-buttons":
                  return <ToggleButtonGroup buttons={props} />;
              }
            })}
          </Box>
        )}
        {download && (
          <Box className={"absolute top-4 right-8"}>
            <DownloadAsPdfButton {...download} variant={"link"} />
          </Box>
        )}
      </VBox>
    </VBox>
  );
}

export type DocumentPreviewProps = {
  content?:
    | { type: "custom"; content: ReactElement }
    | {
        type: "document";
        documentId: string;
        orgNo?: string;
        content?: string;
        url?: string;
        attachments?: AttachmentPreview[];
      }[];
  scrollToId?: string;
  uploadedAttachments?: UploadedAttachmentProps[];
  uploadingAttachments?: UploadingAttachment[];
  isUpdating?: boolean;
  download?: (content: {
    type: "document";
    documentId: string;
    orgNo?: string;
    content?: string;
  }) => DownloadAsPdfButtonProps | undefined;
  downloadAttachment?: (params: {
    documentId: string;
    orgNo?: string;
    type: AttachmentPreview["type"];
  }) => DownloadAsPdfButtonProps | undefined;
  upload?: (
    content: {
      documentId: string;
      orgNo?: string;
    },
    files: File[],
    fileUploadConfig: FileUploadConfig,
  ) => void;
  uploadFileConfig?: FileUploadConfig;
  isUploading?: (content: { documentId: string; orgNo?: string }) => boolean;
  isConverting?: (content: { documentId: string; orgNo?: string }) => boolean;
  centered?: boolean;
  padding?: boolean;
  marginTop?: boolean;
  dropShadow?: boolean;
  extraAction?: (content: {
    type: "document";
    documentId: string;
    orgNo?: string;
    content?: string;
  }) => ["link", LinkButtonProps] | ["toggle-buttons", ToggleButtonProps[]] | undefined;
  onConvert?: (a: UploadedAttachment) => void;
  disableOverflow?: boolean;
};

export const AttachmentsView = observer(
  ({
    content,
    scrollToId,
    uploadedAttachments,
    uploadingAttachments,
    onConvert,
    padding,
    downloadAttachment,
  }: DocumentPreviewProps) => {
    if ((!content || !isArray(content)) && !uploadedAttachments) {
      return undefined;
    }
    const scrollRefs = useRef(new Map<string, HTMLDivElement>());
    useEffect(() => {
      if (!scrollToId) return;
      const el = scrollRefs.current.get(scrollToId);
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [scrollToId]);
    const usePaddingX = !isMobileView() && padding;
    return (
      <>
        {isArray(content) &&
          content.flatMap((c) =>
            c.attachments?.map((a) => (
              <div
                key={a.type}
                ref={(node) => {
                  if (node) {
                    scrollRefs.current.set(a.type, node);
                  } else {
                    scrollRefs.current.delete(a.type);
                  }
                }}
              >
                <DocumentView
                  key={`${c.documentId}-${c.orgNo}-${a.type}`}
                  {...c}
                  dropShadow
                  padding
                  content={a.content}
                  download={downloadAttachment?.({
                    documentId: c.documentId,
                    orgNo: c.orgNo,
                    type: a.type,
                  })}
                />
              </div>
            )),
          )}
        {uploadedAttachments && (
          <VBox className={"w-full"} paddingX={usePaddingX} marginBottom={"lg"}>
            <div className={"w-full"}>
              {uploadedAttachments.map((a) => {
                const isConverting =
                  a.isConverting === true ||
                  uploadingAttachments?.some((u) => u.id === a.id && u.isConverting);
                return (
                  <div
                    key={a.id}
                    ref={(node) => {
                      if (node) {
                        scrollRefs.current.set(a.id, node);
                      } else {
                        scrollRefs.current.delete(a.id);
                      }
                    }}
                  >
                    {showsFilePreview(a.filename, onConvert !== undefined) ? (
                      <FilePreview
                        filename={a.filename}
                        file={{ url: a.downloadUrl! }}
                        width={"auto"}
                        action={
                          a.downloadOriginalUrl ? (
                            <Box justifyContent={"end"} marginX marginY>
                              <LinkButton
                                label={{ key: "download" }}
                                onClick={() => (window.location.href = a.downloadOriginalUrl!)}
                              />
                            </Box>
                          ) : undefined
                        }
                      />
                    ) : (
                      onConvert && (
                        <AttachmentListItem
                          attachment={{
                            ...a,
                            source: isConverting
                              ? { key: "attachments.converting-to-pdf" }
                              : { key: "file-preview.pdf-conversion-available" },
                          }}
                          onOpen={() => {
                            if (!isConverting) {
                              onConvert(a);
                            }
                          }}
                        />
                      )
                    )}
                  </div>
                );
              })}
            </div>
          </VBox>
        )}
      </>
    );
  },
);

export const DocumentPreview = observer(
  ({
    content,
    isUpdating,
    download,
    downloadAttachment,
    centered = true,
    padding = true,
    marginTop,
    dropShadow = true,
    extraAction,
    upload,
    uploadFileConfig,
    isUploading,
    isConverting,
    scrollToId,
    uploadedAttachments,
    uploadingAttachments,
    onConvert,
    disableOverflow,
  }: DocumentPreviewProps) => {
    const { t } = useTranslation();
    const mobile = isMobileView();
    const render = (): ReactElement => {
      switch (true) {
        case content === undefined:
          return (
            <VBox grow={"golden"} justifyContent={"center"}>
              <LoadingIndicator />
            </VBox>
          );

        case Array.isArray(content) &&
          content.length === 1 &&
          isBlank(content[0].content) &&
          content[0].url !== undefined: {
          const pdfDownload = download?.(content[0]);
          return (
            <VBox paddingX={!mobile && padding} grow paddingBottom className={"relative"}>
              <PdfPreview file={{ url: content[0].url }} width={"auto"} />
              {pdfDownload && (
                <Box className={"absolute top-4 right-8"}>
                  <DownloadAsPdfButton {...pdfDownload} variant={"link"} />
                </Box>
              )}
            </VBox>
          );
        }

        case Array.isArray(content) &&
          content.length === 1 &&
          isBlank(content[0].content) &&
          content[0].url === undefined &&
          upload !== undefined:
          return (
            <UploadPdfPlaceholder
              onUpload={(files, fileUploadConfig) => upload(content[0], files, fileUploadConfig)}
              isUploading={isUploading?.(content[0])}
              isConverting={isConverting?.(content[0])}
              fileUploadConfig={uploadFileConfig}
            />
          );

        case Array.isArray(content) &&
          content.length === 1 &&
          !content[0].content &&
          content[0].url === undefined:
          return (
            <VBox grow={"golden"} justifyContent={"center"} fg={"subtle"}>
              <EmptyState
                illustration={"file"}
                title={t("document-preview.missing-preview.title")}
                description={t("document-preview.missing-preview.description")}
                actions={
                  download && (
                    <Button
                      variant={"secondary"}
                      label={t("document-preview.missing-preview.download-pdf")}
                      icon={"download"}
                      onClick={download(content[0])?.onDownload}
                    />
                  )
                }
              />
            </VBox>
          );

        case Array.isArray(content):
          return (
            <VBox className={"relative"}>
              {content.map((c) => (
                <DocumentView
                  key={`${c.documentId}-${c.orgNo}`}
                  {...c}
                  dropShadow={dropShadow}
                  padding={padding}
                  download={download?.(c)}
                  extraAction={extraAction?.(c)}
                />
              ))}
              <AttachmentsView
                content={content}
                scrollToId={scrollToId}
                uploadedAttachments={uploadedAttachments}
                uploadingAttachments={uploadingAttachments}
                onConvert={onConvert}
                padding={padding}
                downloadAttachment={downloadAttachment}
              />
              {isUpdating && <LoadingOverlay />}
            </VBox>
          );

        default:
          return (
            <VBox paddingX={!mobile && padding} grow className={"relative"}>
              {content.content}
            </VBox>
          );
      }
    };

    return (
      <VBox
        grow
        overflowY={mobile || disableOverflow ? undefined : "auto"}
        className={"w-full"}
        marginTop={marginTop}
      >
        <VBox
          grow
          marginX={centered ? "auto" : undefined}
          className={classNames("w-full", centered && "max-w-[900px]")}
        >
          {render()}
        </VBox>
      </VBox>
    );
  },
);
